import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { HarnessDistributionRow } from '../../api/types';

interface Props {
  harnesses: HarnessDistributionRow[];
  loading?: boolean;
}

const HARNESS_COLORS: Record<string, string> = {
  'slicc': '#6366f1',       // indigo
  'claude-code': '#f59e0b', // amber
  'copilot': '#10b981',     // emerald
  'pi': '#0ea5e9',          // sky
  'other': '#94a3b8',       // slate
};

function colorFor(harness: string): string {
  return HARNESS_COLORS[harness] ?? '#94a3b8';
}

export function HarnessDistributionDonut({ harnesses, loading }: Props): JSX.Element {
  if (!loading && harnesses.length === 0) {
    return <div className="model-distribution-empty">No harness data in this window.</div>;
  }

  const total = harnesses.reduce((s, h) => s + h.events, 0);

  const option: EChartsOption = {
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        const p = Array.isArray(params) ? params[0] : params;
        if (!p) return '';
        const value = p.value as number;
        const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
        const row = harnesses.find((h) => h.harness === p.name);
        return `<strong>${p.name}</strong><br/>${value.toLocaleString()} events (${pct}%)<br/>${row?.users ?? 0} users`;
      },
    },
    legend: {
      bottom: 0,
      icon: 'circle',
      itemGap: 16,
    },
    series: [
      {
        name: 'Events by harness',
        type: 'pie',
        radius: ['52%', '78%'],
        center: ['50%', '44%'],
        avoidLabelOverlap: true,
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 2,
        },
        label: {
          show: true,
          position: 'outside',
          formatter: (p) => {
            const value = p.value as number;
            if (total === 0) return '';
            const pct = (value / total) * 100;
            return pct >= 5 ? `${p.name}\n${pct.toFixed(0)}%` : '';
          },
          fontSize: 12,
          color: 'inherit',
        },
        labelLine: { show: true, length: 8, length2: 6 },
        data: harnesses.map((h) => ({
          name: h.harness,
          value: h.events,
          itemStyle: { color: colorFor(h.harness) },
        })),
      },
    ],
    graphic: [
      {
        type: 'text',
        left: 'center',
        top: '38%',
        style: {
          text: total.toLocaleString(),
          align: 'center',
          fontSize: 22,
          fontWeight: 700,
          fill: '#111',
        },
      },
      {
        type: 'text',
        left: 'center',
        top: '49%',
        style: {
          text: 'events',
          align: 'center',
          fontSize: 11,
          fill: '#888',
        },
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: 408, width: '100%' }}
      showLoading={loading}
      notMerge
      lazyUpdate
    />
  );
}
